import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "../common/Reveal";
import { Img } from "../common/Img";
import { InfoModal } from "../modals/Modals";
import { MARKETS } from "../../data/markets";
import { useLanguage } from "../../context/LanguageContext";

const PREMIUM_EASE = [0.22, 1, 0.36, 1];

const horizontalTitleMotion = {
  initial: {
    opacity: 0,
    x: 42,
    filter: "blur(9px)",
    clipPath: "inset(0 0 0 100%)",
  },
  animate: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    clipPath: "inset(0 0 0 0%)",
    transition: {
      duration: 1.28,
      delay: 0.18,
      ease: PREMIUM_EASE,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: PREMIUM_EASE,
    },
  },
};

const horizontalDescriptionMotion = {
  initial: {
    opacity: 0,
    x: 32,
    filter: "blur(7px)",
    clipPath: "inset(0 0 0 100%)",
  },
  animate: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    clipPath: "inset(0 0 0 0%)",
    transition: {
      duration: 1.38,
      delay: 0.34,
      ease: PREMIUM_EASE,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.18,
      ease: PREMIUM_EASE,
    },
  },
};

function MarketCopy({ market, active, localize }) {
  return (
    <div className="relative min-h-[138px]">
      <motion.div
        initial={false}
        animate={{
          opacity: active ? 0 : 1,
        }}
        transition={{
          duration: active ? 0.26 : 0.34,
          delay: active ? 0 : 0.08,
          ease: PREMIUM_EASE,
        }}
        className="absolute bottom-0 left-0"
        aria-hidden={active}
      >
        <h3 className="rotate-180 font-serif text-xl leading-tight text-[#F6F2EC] [writing-mode:vertical-rl]">
          {localize(market, "name")}
        </h3>
      </motion.div>

      <AnimatePresence initial={false}>
        {active ? (
          <motion.div
            key={`horizontal-${market.id}`}
            className="absolute bottom-0 left-0 max-w-lg overflow-hidden"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.h3
              variants={horizontalTitleMotion}
              initial="initial"
              animate="animate"
              exit="exit"
              className="font-serif text-3xl leading-tight text-[#F6F2EC]"
            >
              {localize(market, "name")}
            </motion.h3>

            <motion.p
              variants={horizontalDescriptionMotion}
              initial="initial"
              animate="animate"
              exit="exit"
              className="mt-3 max-w-md text-sm leading-relaxed text-[#F6F2EC]/80"
            >
              {localize(market, "description")}
            </motion.p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function MobileMarketCopy({ market, localize, index }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: 36,
        filter: "blur(8px)",
        clipPath: "inset(0 0 0 100%)",
      }}
      whileInView={{
        opacity: 1,
        x: 0,
        filter: "blur(0px)",
        clipPath: "inset(0 0 0 0%)",
      }}
      viewport={{ once: true, amount: 0.45 }}
      transition={{
        duration: 1.2,
        delay: index === 0 ? 0.08 : 0.16,
        ease: PREMIUM_EASE,
      }}
    >
      <h3 className="font-serif text-2xl text-[#F6F2EC]">
        {localize(market, "name")}
      </h3>
      <p className="mt-2 text-sm text-[#F6F2EC]/80">
        {localize(market, "description")}
      </p>
    </motion.div>
  );
}

export default function Markets() {
  const { t, localize } = useLanguage();
  const [active, setActive] = useState(0);
  const [modal, setModal] = useState(false);

  return (
    <section className="bg-black py-24 md:py-32" data-testid="markets-section">
      <div className="container-tessa">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#C4A46B]">
              {t("markets.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-4xl leading-[1.0] text-[#F6F2EC] md:text-5xl lg:text-6xl">
              {t("markets.title1")}
              <br />
              <span className="italic">{t("markets.title2")}</span>
            </h2>
          </Reveal>
          <button
            onClick={() => setModal(true)}
            data-testid="see-all-markets"
            className="self-start border-b border-[#C4A46B] pb-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#F6F2EC] transition-colors hover:text-[#C4A46B] md:self-end"
          >
            {t("markets.seeAll")}
          </button>
        </div>

        <div className="hidden h-[520px] gap-2 md:flex">
          {MARKETS.map((market, index) => {
            const isActive = active === index;
            return (
              <button
                key={market.id}
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onClick={() => setActive(index)}
                data-testid={`market-${market.id}`}
                aria-expanded={isActive}
                className="relative overflow-hidden text-left transition-[flex-grow] duration-[950ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                style={{ flexGrow: isActive ? 4 : 1, flexBasis: 0 }}
              >
                <Img
                  src={market.image}
                  alt={localize(market, "name")}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <MarketCopy market={market} active={isActive} localize={localize} />
                </div>
              </button>
            );
          })}
        </div>

        <div className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] md:hidden">
          {MARKETS.map((market, index) => (
            <div
              key={market.id}
              data-testid={`market-mobile-${market.id}`}
              className="relative aspect-[3/4] w-[78vw] shrink-0 snap-center overflow-hidden"
            >
              <Img
                src={market.image}
                alt={localize(market, "name")}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end overflow-hidden p-5">
                <MobileMarketCopy market={market} localize={localize} index={index} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <InfoModal open={modal} onClose={() => setModal(false)} title={t("markets.eyebrow")}>
        <ul className="space-y-3">
          {MARKETS.map((market) => (
            <li key={market.id} className="border-b border-black/10 pb-3">
              <span className="font-serif text-xl text-[#252525]">
                {localize(market, "name")}
              </span>
              <p className="mt-1 text-sm text-[#817B74]">
                {localize(market, "description")}
              </p>
            </li>
          ))}
        </ul>
      </InfoModal>
    </section>
  );
}
